import type { Expression, FilterSpecification, Map } from "mapbox-gl"
import type { Filters } from "@/model/RootStore"

export const FLOODABLE_ZONES_COLOR = "#1E88E5"
export const WETLANDS_COLOR = "#2A8C86"

interface EnvironmentLayerOptions {
	baseUrl: string
	sourceName: string
	layerName: string
	visibleLayers: string[]
}

export function addFloodableZones(
	map: Map,
	{ baseUrl, visibleLayers, layerName, sourceName }: EnvironmentLayerOptions
) {
	map.addSource(sourceName, {
		tiles: [`${baseUrl}/landerz_models.floodable_area_polygons/{z}/{x}/{y}.pbf`],
		type: "vector",
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.floodable_area_polygons",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("floodable-zones") ? "visible" : "none",
		},
		paint: {
			"fill-opacity": 0.2,
			"fill-color": FLOODABLE_ZONES_COLOR,
		},
		metadata: { custom: true },
	})
}

export function addWetlands(
	map: Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
		isSatellite = false,
	}: EnvironmentLayerOptions & { isSatellite?: boolean }
) {
	const visibility = visibleLayers.includes("wetlands") ? "visible" : "none"
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/landerz_models.milieux_humides_sudqc_2022/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "landerz_models.milieux_humides_sudqc_2022",
		type: "fill",
		layout: { visibility },
		paint: {
			"fill-opacity": isSatellite ? 0.55 : 0.3,
			"fill-color": WETLANDS_COLOR,
		},
		minzoom: 13,
		metadata: { custom: true },
	})
	map.addLayer({
		id: `${layerName}_line`,
		source: sourceName,
		"source-layer": "landerz_models.milieux_humides_sudqc_2022",
		type: "line",
		layout: { visibility },
		paint: {
			"line-color": WETLANDS_COLOR,
			"line-width": isSatellite ? 1 : 0.5,
			"line-opacity": isSatellite ? 0.9 : 0.6,
		},
		minzoom: 13,
		metadata: { custom: true },
	})
}

type EndangeredSpeciesFilters = Filters["endangeredSpecies"]

export const endangeredSpeciesLayerDefinitions: {
	id: string
	filterKey: keyof EndangeredSpeciesFilters
	labelKey: string
	classes: string[]
	fillColor: string
	lineColor: string
	fillOpacity: number
}[] = [
	{
		id: "endangered-species",
		filterKey: "threatenedOrVulnerable",
		labelKey: "endangered-species.level.threatened-or-vulnerable",
		classes: ["Menacée", "Vulnérable"],
		fillColor: "#C84141",
		lineColor: "#8A2424",
		fillOpacity: 0.36,
	},
	{
		id: "endangered-species-susceptible",
		filterKey: "susceptible",
		labelKey: "endangered-species.level.susceptible",
		classes: ["Susceptible"],
		fillColor: "#D97904",
		lineColor: "#9A5200",
		fillOpacity: 0.32,
	},
	{
		id: "endangered-species-candidate",
		filterKey: "candidate",
		labelKey: "endangered-species.level.candidate",
		classes: ["Candidate"],
		fillColor: "#6C8F2D",
		lineColor: "#49631D",
		fillOpacity: 0.3,
	},
	{
		id: "endangered-species-masked",
		filterKey: "masked",
		labelKey: "endangered-species.level.masked",
		classes: ["Masquée"],
		fillColor: "#7F7F7F",
		lineColor: "#555555",
		fillOpacity: 0.26,
	},
]

export function getEndangeredSpeciesFilter(
	classes: string[],
	speciesType?: "Animaux" | "Plantes"
): FilterSpecification {
	const conditions: FilterSpecification[] = [["in", ["get", "species_vulnerability_class"], ["literal", classes]]]

	if (speciesType) {
		conditions.push(["==", ["get", "species_type"], speciesType])
	}

	return ["all", ...conditions]
}

export function getDisabledEndangeredSpeciesFilter(): FilterSpecification {
	return ["==", ["get", "species_vulnerability_class"], "__none__"]
}

export function getActiveEndangeredSpeciesFilter(enabledSpecies: EndangeredSpeciesFilters): FilterSpecification {
	const activeClasses = endangeredSpeciesLayerDefinitions.flatMap(definition =>
		enabledSpecies[definition.filterKey] ? definition.classes : []
	)

	if (activeClasses.length === 0) {
		return getDisabledEndangeredSpeciesFilter()
	}

	return ["in", ["get", "species_vulnerability_class"], ["literal", activeClasses]]
}

export function getEndangeredSpeciesLabelField(locale: string): Expression {
	return [
		"coalesce",
		["to-string", ["get", locale === "en" ? "name_en" : "name_fr"]],
		["to-string", ["get", locale === "en" ? "name_fr" : "name_en"]],
		"",
	]
}

export function addEndangeredSpecies(
	map: Map,
	{
		baseUrl,
		visibleLayers,
		sourceName,
		enabledSpecies,
		locale,
	}: {
		baseUrl: string
		sourceName: string
		visibleLayers: string[]
		enabledSpecies: EndangeredSpeciesFilters
		locale: string
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.endangered_species/{z}/{x}/{y}.pbf`],
	})

	const isVisible = visibleLayers.includes("endangered-species")

	for (const definition of endangeredSpeciesLayerDefinitions) {
		const isEnabled = enabledSpecies[definition.filterKey]

		map.addLayer({
			id: definition.id,
			source: sourceName,
			"source-layer": "core_public.endangered_species",
			type: "fill",
			filter: isEnabled
				? getEndangeredSpeciesFilter(definition.classes, "Animaux")
				: getDisabledEndangeredSpeciesFilter(),
			layout: {
				visibility: isVisible ? "visible" : "none",
			},
			paint: {
				"fill-color": definition.fillColor,
				"fill-opacity": definition.fillOpacity,
			},
			metadata: { custom: true },
		})

		map.addLayer({
			id: `${definition.id}-plants`,
			source: sourceName,
			"source-layer": "core_public.endangered_species",
			type: "fill",
			filter: isEnabled
				? getEndangeredSpeciesFilter(definition.classes, "Plantes")
				: getDisabledEndangeredSpeciesFilter(),
			layout: {
				visibility: isVisible ? "visible" : "none",
			},
			paint: {
				"fill-pattern": `${definition.id}-plant-hatch`,
				"fill-opacity": 1,
			},
			metadata: { custom: true },
		})

		map.addLayer({
			id: `${definition.id}-line`,
			source: sourceName,
			"source-layer": "core_public.endangered_species",
			type: "line",
			filter: isEnabled ? getEndangeredSpeciesFilter(definition.classes) : getDisabledEndangeredSpeciesFilter(),
			layout: {
				visibility: isVisible ? "visible" : "none",
				"line-join": "round",
				"line-cap": "round",
			},
			paint: {
				"line-color": definition.lineColor,
				"line-opacity": 0.85,
				"line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.5, 13, 1.25, 16, 2.5],
			},
			metadata: { custom: true },
		})
	}

	map.addLayer({
		id: "endangered-species-labels",
		source: sourceName,
		"source-layer": "core_public.endangered_species",
		type: "symbol",
		filter: getActiveEndangeredSpeciesFilter(enabledSpecies),
		layout: {
			"text-field": getEndangeredSpeciesLabelField(locale),
			"text-size": ["interpolate", ["linear"], ["zoom"], 11, 10, 14, 12, 16, 14],
			"text-allow-overlap": false,
			"symbol-placement": "point",
			visibility: isVisible ? "visible" : "none",
		},
		paint: {
			"text-color": "#26301B",
			"text-halo-color": "#FFFFFF",
			"text-halo-width": 2,
		},
		minzoom: 11,
		metadata: { custom: true },
	})
}
